import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { uniq } from 'lodash';
import { MDBDatePickerComponent, ToastService } from 'ng-uikit-pro-standard';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { CondicionPagoService } from 'src/app/services/condicion-pago.service';
import { DataCafService } from 'src/app/services/data-caf.service';
import { DireccionService } from 'src/app/services/direccion.service';
import { DocumentoService, IFechaVencimientoQueryParams } from 'src/app/services/documento.service';
import { MessageRendererService } from 'src/app/services/message-renderer.service';
import { VendedorRubroClienteService } from 'src/app/services/vendedor-rubro-cliente.service';
import { VendedorService } from 'src/app/services/vendedor.service';
import { unidad_negocio } from 'src/app/shared/object/unidad_negocio.model';
import { ConsoleService } from 'src/app/shared/services/console.service';
import { DatesService } from 'src/app/shared/services/dates.service';
import { FormValidatorsService } from 'src/app/shared/services/form-validators.service';
import { GraphQLClient } from 'src/app/shared/services/graph-qlclient.service';
import { RutService } from 'src/app/shared/services/rut.service';
import { SecurityService } from 'src/app/shared/services/security.service';
import { ModalDocsReferenciaComponent } from 'src/app/views/shared/modal-docs-referencia/modal-docs-referencia.component';
import { ModalSeleccionarClienteComponent } from 'src/app/views/shared/modal-seleccionar-cliente/modal-seleccionar-cliente.component';

@Component({
  selector: 'app-factura-manual-cabecera',
  templateUrl: './factura-manual-cabecera.component.html',
  styleUrls: ['./factura-manual-cabecera.component.scss'],
  providers: [SecurityService],
})
export class FacturaManualCabeceraComponent implements OnInit {
  @Input() permiteUnidadNegocio: boolean = false;
  @Input() optionsUnidadesDeNegocio: unidad_negocio[] = [];

  @Output() onClienteChange: EventEmitter<any> = new EventEmitter<any>();

  // Modales
  @ViewChild(ModalSeleccionarClienteComponent, { static: true }) modalSeleccionarCliente: ModalSeleccionarClienteComponent;
  @ViewChild(ModalDocsReferenciaComponent, { static: true }) modalDocsReferencias: ModalDocsReferenciaComponent;
  @ViewChild('datePicker', { static: true }) datePickerFechaDoc: MDBDatePickerComponent;

  // Contants
  readonly FACTURA_AFECTA: String = '_FACTURA_AFECTA_';
  readonly FACTURA_EXCENTA: String = '_FACTURA_EXCENTA_';
  readonly DESCUENTO_ITEM_AFECTO: String = '_DESCUENTO_ITEM_AFECTO_';
  readonly DESCUENTO_ITEM_EXCENTO: String = '_DESCUENTO_ITEM_EXCENTO_';
  readonly RECARGO_ITEM_AFECTO: String = '_RECARGO_ITEM_AFECTO_';
  readonly RECARGO_ITEM_EXCENTO: String = '_RECARGO_ITEM_EXCENTO_';

  // Form
  formHeader: FormGroup;

  // Data &  Tmp
  private rutEmpresa: number;
  private sucursalEmpresa: number;
  enableClient: boolean = true;
  optionsSucursal: Array<any> = [];
  optionsVendedor: Array<any> = [];
  optionsCondicionPago: Array<any> = [];
  headerData: any = {};
  private isCleaningUp: boolean = false;
  CountListDocRef: number = 0;
  documentRef: Array<any> = [];

  // public getters
  get cliente() {
    if (!this.headerData) return null;
    return this.headerData.clienteSeleccionado;
  }

  // Utils
  private log: any;

  constructor(
    private dataCafService: DataCafService,
    private gqlclient: GraphQLClient,
    private accesolocalstorage: SecurityService,
    private fb: FormBuilder,
    private clog: ConsoleService,
    private rutService: RutService,
    private direccionService: DireccionService,
    private vendedorService: VendedorService,
    private vendedorRubroClienteService: VendedorRubroClienteService,
    private toastrService: ToastService,
    private messageRendererService: MessageRendererService,
    private documentoService: DocumentoService,
    private datesService: DatesService,
    private formValidatorsService: FormValidatorsService,
    private condicionPagoService: CondicionPagoService
  ) {
    this.log = this.clog.log('CABECERA');
  }

  ngOnInit() {
    this.rutEmpresa = Number(this.accesolocalstorage.getRutEmpresa());
    this.sucursalEmpresa = Number(this.accesolocalstorage.getSucursalCodigo());

    this.fetchCondicionPago();
    this.fetchVendedor();
    this.createForms();
    this.formChangesSubscriptions();
  }

  private fetchCondicionPago() {
    const inputParams = {
      rut_empresa: this.rutEmpresa,
      sucursal_codigo: this.sucursalEmpresa,
      tipobusqueda: 1,
    };

    this.log('fetch condición pago', JSON.stringify(inputParams));
    this.condicionPagoService.obtenerSegunFiltro(inputParams, ({ mensajes, respuesta }) => {
      const ok = respuesta !== null;
      if (!ok) {
        this.messageRendererService.showErrors(mensajes);
        return;
      }

      const { registros } = respuesta;
      this.optionsCondicionPago = registros.map((condicionPago) => ({
        value: condicionPago.codigo,
        label: condicionPago.descripcion,
        ...condicionPago,
      }));

      this.formValidatorsService.selectFirstOrDefault(this.formHeader.get('condicionPago'), this.optionsCondicionPago);
    });
  }

  private fetchVendedor() {
    this.vendedorService.obtenerActivos(this.rutEmpresa, this.sucursalEmpresa, ({ mensajes, respuesta }) => {
      const ok = respuesta !== null;
      if (!ok) {
        this.messageRendererService.showErrors(mensajes);
        return;
      }

      const { registros } = respuesta;
      this.optionsVendedor = registros.map((vendedor) => ({
        value: vendedor.codigo,
        label: vendedor.nombre,
        ...vendedor,
      }));

      this.formValidatorsService.selectFirstOrDefault(this.formHeader.get('vendedor'), this.optionsVendedor);
    });
  }

  private createForms() {
    this.formHeader = this.fb.group({
      unidad_negocio_codigo: [null, [Validators.required]],
      rutCliente: [null, [Validators.required]],
      sucursal: [null, [Validators.required]],
      condicionPago: [null, [Validators.required]],
      fechaFactura: [null, [Validators.required]],
      facturaAfectaOrExcenta: [this.FACTURA_AFECTA, [Validators.required]],
      incluirEnRecorrido: [true, [Validators.required]],
      vendedor: [null, [Validators.required]],
      descuento: this.fb.group({
        porcentaje: [null],
        monto: [null],
        afectoOrExcento: [this.DESCUENTO_ITEM_AFECTO, [Validators.required]],
      }),
      recargo: this.fb.group({
        porcentaje: [null],
        monto: [null],
        afectoOrExcento: [this.RECARGO_ITEM_AFECTO, [Validators.required]],
      }),
    });
  }

  /**
   * Configura las suscripciones a los cambios de valores en el formulario.
   * Maneja la búsqueda automática de cliente por RUT con debounce para evitar múltiples llamadas.
   * También maneja los cambios en fecha de factura y sucursal.
   */
  private formChangesSubscriptions() {
    // Suscripción al campo RUT con debounce para buscar cliente automáticamente
    this.formHeader
      .get('rutCliente')
      .valueChanges.pipe(filter(Boolean), debounceTime(1000))
      .subscribe((rutCliente: string) => {
        // Limpiar el último RUT buscado ANTES de buscar para permitir buscar el mismo RUT nuevamente
        this.modalSeleccionarCliente.cleanLast();
        // El método searchByRut maneja internamente la validación y limpieza del RUT
        this.modalSeleccionarCliente.searchByRut(rutCliente);
      });

    this.formHeader.get('fechaFactura').valueChanges.subscribe((fecha) => this.handleFechaFacturaChange(fecha));
    this.formHeader.get('sucursal').valueChanges.subscribe((direccion) => this.handleDireccionChange(direccion));
    this.formHeader.get('condicionPago').valueChanges.subscribe((condicionPago) => {
      if (!condicionPago) {
        this.log('condicionPago change', condicionPago);
        if (this.headerData && this.headerData.fechaVencimiento) this.headerData.fechaVencimiento = null;
      } else this.fetchFechaVencimiento();
    });

    // toggle descuentos y recargos | descuento | porcentaje/pesos clp
    this.formHeader
      .get('descuento.porcentaje')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.togglePorcentajeMontoValuesAndValidators(value, 'descuento.porcentaje', 'descuento.monto', true, this.headerData.clienteSeleccionado);
      });
    this.formHeader
      .get('descuento.monto')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.togglePorcentajeMontoValuesAndValidators(value, 'descuento.monto', 'descuento.porcentaje', false);
      });

    // toggle descuentos y recargos | recargo | porcentaje/pesos clp
    this.formHeader
      .get('recargo.porcentaje')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.togglePorcentajeMontoValuesAndValidators(value, 'recargo.porcentaje', 'recargo.monto', true);
      });
    this.formHeader
      .get('recargo.monto')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.togglePorcentajeMontoValuesAndValidators(value, 'recargo.monto', 'recargo.porcentaje', false);
      });
    this.formHeader.get('facturaAfectaOrExcenta')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((x: string) => {
        this.obtenerCodigoDteSeleccionado(x);
      })
    this.formHeader.get("facturaAfectaOrExcenta").setValue(this.FACTURA_AFECTA);
  }

  // Obtener el codigo DTE seleccionado para setearlo en el servicio de DataCAF
  obtenerCodigoDteSeleccionado(value: string) {
    const _facturaAfecta = value == this.FACTURA_AFECTA; // == y no === dado que son tipos distintos
    const codigo_dte = _facturaAfecta ? 33 : 34; // AFECTA:33, EXCENTA:34
    this.dataCafService.setDataCodigoDTE(codigo_dte);
  }

  private togglePorcentajeMontoValuesAndValidators(value, currentFormControlName, previousFormControlName, isPorcentaje, cliente = null) {
    if (value === null) return;
    // this.log('togglePorcentajeMontoValuesAndValidators', 'value', value, 'currentFormControlName', currentFormControlName, 'previousFormControlName', previousFormControlName, 'isPorcentaje', isPorcentaje)
    if (value) {
      // remove validators
      // this.formHeader.get(currentFormControlName).setValidators(null)
      this.formHeader.get(previousFormControlName).setValidators(null);
      this.formHeader.get(previousFormControlName).setValue(null);
      this.formHeader.get(previousFormControlName).updateValueAndValidity();

      // set validator
      const validators = [Validators.min(0)];
      if (isPorcentaje) {
        if (cliente) {
          const { descuento_maximo } = this.headerData.clienteSeleccionado;
          if (descuento_maximo > 0) validators.push(Validators.max(descuento_maximo));
          else validators.push(Validators.max(99.9999));
        } else validators.push(Validators.max(99.9999));
      }
      this.formHeader.get(currentFormControlName).setValidators(validators);
      this.formHeader.get(currentFormControlName).updateValueAndValidity();
    }
  }

  //ABRIR MODAL TABLA DE DOCS. REFERENCIA
  abrirModalDocsReferencia() {
    //enviar fecha que viene en rut cliente
    const fechaDoc = this.formHeader.get('fechaFactura').value;
    this.modalDocsReferencias.show(fechaDoc);
  }

  /**
   * Abre el modal de búsqueda de clientes.
   * Permite al usuario buscar y seleccionar un cliente de manera manual.
   */
  abrirModalSeleccionarCliente() {
    // Validar que el modal esté disponible antes de abrirlo
    if (this.modalSeleccionarCliente) {
      this.modalSeleccionarCliente.show();
    } else {
      this.log('Error: modalSeleccionarCliente no está disponible');
    }
  }


  /**
   * Maneja la selección de un cliente desde el modal de búsqueda.
   * Establece los datos del cliente seleccionado y configura los valores por defecto
   * en el formulario basándose en la información del cliente.
   *
   * @param data - Datos del cliente seleccionado
   * @param origen - Origen de la selección (modal, búsqueda, etc.)
   */
  onClienteSeleccionado({ data, origen }) {
    // Validar que se recibieron datos válidos
    if (!data) {
      this.log('onClienteSeleccionado: datos inválidos', data);
      return;
    }

    // Limpiar datos anteriores y establecer el nuevo cliente
    this.headerData = {};
    this.headerData.clienteSeleccionado = data;

    // Formatear el RUT del cliente y establecerlo en el formulario
    this.headerData.clienteSeleccionado.rutFormato = this.rutService.AgregarDV(this.headerData.clienteSeleccionado.rut);
    this.formHeader.get('rutCliente').setValue(this.headerData.clienteSeleccionado.rutFormato, { emitEvent: false });

    // Establecer fecha de factura por defecto (fecha actual)
    this.formHeader.get('fechaFactura').setValue(this.datesService.CurrentDateToShortTimeString());

    // Establecer descuento máximo del cliente (convertir punto a coma para formato chileno)
    if (this.headerData.clienteSeleccionado.descuento_maximo !== null && this.headerData.clienteSeleccionado.descuento_maximo !== undefined) {
      this.formHeader.get('descuento.porcentaje').setValue(this.headerData.clienteSeleccionado.descuento_maximo.toString().replace('.', ','));
    }

    // Emitir evento de cambio de cliente para que otros componentes puedan reaccionar
    this.onClienteChange.emit(this.headerData.clienteSeleccionado);

    // Buscar sucursales asociadas al cliente
    this.fetchSucursal(this.headerData.clienteSeleccionado.rut);
  }

  /**
   * Maneja el caso cuando no se encuentra un cliente al buscar por RUT.
   * Limpia los datos del cliente si existen.
   */
  onClienteNoEncontrado() {
    if (this.headerData && this.headerData.clienteSeleccionado) {
      this.clearClientData();
    }
  }

  /**
   * Limpia los datos del cliente seleccionado y emite un evento indicando que no hay cliente.
   * Se utiliza cuando se busca un cliente y no se encuentra, o cuando se necesita limpiar
   * la selección de cliente sin limpiar todo el formulario.
   */
  clearClientData() {
    // Limpiar solo los datos del cliente, manteniendo otros valores del formulario
    if (this.headerData) {
      this.headerData.clienteSeleccionado = null;
    }
    this.formHeader.get('rutCliente').setValue(null, { emitEvent: false });
    this.optionsSucursal = [];
    this.onClienteChange.emit(null);
  }

  private fetchSucursal(cliente_proveedor_rut) {
    const inputParams = {
      empresa_rut: this.rutEmpresa,
      // sucursal_codigo: this.sucursalEmpresa,
      cliente_proveedor_rut,
    };

    this.log('fetch sucursal', JSON.stringify(inputParams));
    this.direccionService.GetByCliente(inputParams, ({ registros }) => {
      this.optionsSucursal = registros.map((direccion) => ({
        value: direccion.codigo,
        label: direccion.direccion_completa,
        ...direccion,
      }));
      this.formValidatorsService.selectFirstOrDefault(this.formHeader.get('sucursal'), this.optionsSucursal);
    });
  }

  // private handlePorcentajeMaximoDescuento({ descuento_maximo }) {
  //   const actual = this.formHeader.get("descuento.porcentaje").value
  //   if (this.formHeader.get("descuento.porcentaje").value)
  //     this.formHeader.get("descuento.porcentaje").setValue(this.formHeader.get("descuento.porcentaje").value)
  // }

  private handleDireccionChange(value) {
    this.log('handleDireccionChange', value);
    if (!this.headerData || !this.headerData.clienteSeleccionado || !this.optionsSucursal) return;

    this.headerData.direccion = this.optionsSucursal.filter((p) => p.value === value)[0];

    // this.log('this.headerData.direccion', this.headerData.direccion)
    this.fetchVendedorByDefautByCliente(this.headerData.clienteSeleccionado.rut, this.headerData.direccion.codigo);

    // this.log('dirección', this.headerData.direccion)
    // this.log('lista cond.Pago', this.optionsCondicionPago)
    // obtener la sucursal seleccionada
    // const sucursalSeleccionada = this.optionsSucursal.filter(p => p.codigo === value)
    // if (sucursalSeleccionada && sucursalSeleccionada.length > 0) {

    // set default condicionPago
    // condicion_pago_codigo
    const condicionPagoSucursal = this.optionsCondicionPago.filter((p) => p.codigo === this.headerData.direccion.condicion_pago_codigo);
    if (condicionPagoSucursal && condicionPagoSucursal.length > 0) this.formHeader.get('condicionPago').setValue(condicionPagoSucursal[0].value);
    // }
    // this.fetchFechaVencimiento()
  }

  private fetchVendedorByDefautByCliente(rut, codigo_direccion) {
    const inputParams: any = {
      rutEmpresa: this.rutEmpresa,
      sucursalCodigo: this.sucursalEmpresa,
      rutCliente: rut,
      direccionCodigo: codigo_direccion,
    };

    this.log('fetch vendedor', JSON.stringify(inputParams));

    if (!this.isCleaningUp) {
      this.vendedorRubroClienteService.GetByCliente(inputParams, ({ registros }) => {
        const _cobradores: any[] = registros.filter((p) => p.es_cobrador);
        const uniqVendedores = uniq(registros.map((p) => p.vendedor_codigo));
        if (_cobradores && _cobradores.length > 0) {
          const vendedorCobradorSucursal = this.optionsVendedor.filter((p) => p.codigo === _cobradores[0].vendedor_codigo);
          if (vendedorCobradorSucursal && vendedorCobradorSucursal.length > 0) this.formHeader.get('vendedor').setValue(vendedorCobradorSucursal[0].codigo);
        } else if (registros.every((p) => p.es_cobrador === false) && uniqVendedores.length === 1) { // VERIFICAR SI SOLO EXISTE UN VENDEDOR Y QUE NO ES COBRADOR
          const vendedorUniq = this.optionsVendedor.filter((p) => p.codigo === uniqVendedores[0]);
          if (vendedorUniq && vendedorUniq.length > 0) this.formHeader.get('vendedor').setValue(vendedorUniq[0].codigo);
        } else {
          this.log('No se encontró vendedor cobrador, se selecciona el primero por defecto', JSON.stringify(registros));
        }
      });
    }

    // else {
    //   this.formValidatorsService.selectFirstOrDefault(this.formHeader.get('vendedor'), this.optionsVendedor);
    // }
  }

  private handleFechaFacturaChange(fecha) {
    this.headerData.fechaVencimiento = null;
    if (!fecha)
      // TODO Validar que sea fecha válida
      return;
    this.fetchFechaVencimiento();
  }

  private fetchFechaVencimiento() {
    this.headerData.fechaVencimiento = null;
    this.log('fetchFechaVencimiento', this.formHeader.get('fechaFactura').value, this.formHeader.get('condicionPago').value, this.headerData);
    if (
      !this.headerData ||
      // !this.headerData.clienteSeleccionado ||
      // !this.headerData.direccion ||
      !this.formHeader.get('fechaFactura').value ||
      !this.formHeader.get('condicionPago').value
    )
      return;

    const inputParams: IFechaVencimientoQueryParams = {
      rut_empresa: this.rutEmpresa,
      sucursal_codigo: this.sucursalEmpresa,
      cliente_proveedor_rut: 0,
      // direccion_codigo: this.headerData.clienteSeleccionado.codigo_direccion,
      direccion_codigo: 0,
      fecha_documento: this.formHeader.get('fechaFactura').value,
      condicion_pago_codigo: this.formHeader.get('condicionPago').value,
    };

    this.log('fetchFechaVencimiento', JSON.stringify(inputParams));
    this.documentoService.ObtenerFechaVencimiento(inputParams, ({ mensajes, respuesta }) => {
      this.headerData.fechaVencimiento = respuesta.fecha_vencimiento;
    });
  }

  validate() {
    // this.log('descuento.porcentaje', this.formHeader.get('descuento.porcentaje').valid)
    // this.log('descuento.monto', this.formHeader.get('descuento.monto').valid)
    // this.log('recargo.porcentaje', this.formHeader.get('recargo.porcentaje').valid)
    // this.log('recargo.monto', this.formHeader.get('recargo.monto').valid)

    const unidad_negocio_codigo_state = this.formHeader.get('unidad_negocio_codigo').enabled;
    this.formHeader.get('unidad_negocio_codigo').enable();

    // Add/Remove unidad_negocio_codigo validators
    if (this.permiteUnidadNegocio) {
      this.formHeader.get('unidad_negocio_codigo').setValidators([Validators.required]);
    } else {
      this.formHeader.get('unidad_negocio_codigo').setValidators([]);
    }
    this.formHeader.get('unidad_negocio_codigo').updateValueAndValidity();

    const valid = this.formValidatorsService.validate(this.formHeader);
    if (!unidad_negocio_codigo_state) this.formHeader.get('unidad_negocio_codigo').disable();
    return valid;
  }

  getHeaderData() {
    // this.documentRef.map(x=> {
    //   delete x.checked
    //   delete x.index
    // })
    this.isCleaningUp = true;

    const descuento_EsPorcentaje = this.formHeader.get('descuento.porcentaje').value !== null;
    const recargo_EsPorcentaje = this.formHeader.get('recargo.porcentaje').value !== null;

    const facturaAfectaOrExcentaEnabled: boolean = this.formHeader.get('facturaAfectaOrExcenta').enabled;

    this.formHeader.get('facturaAfectaOrExcenta').enable();
    const { facturaAfectaOrExcenta } = this.formHeader.value;
    const _facturaAfecta = facturaAfectaOrExcenta == this.FACTURA_AFECTA; // == y no === dado que son tipos distintos
    const tipo_dte_codigo = _facturaAfecta ? 33 : 34; // AFECTA:33, EXCENTA:34
    this.log('_facturaAfecta', _facturaAfecta, 'tipo_dte_codigo', tipo_dte_codigo);
    if (!facturaAfectaOrExcentaEnabled) this.formHeader.get('facturaAfectaOrExcenta').disable();

    return {
      unidad_negocio_codigo: this.formValidatorsService.obtenerValorFormHeader(this.formHeader, 'unidad_negocio_codigo'),
      cliente: this.cliente,
      facturaAfecta: _facturaAfecta, //facturaAfectaOrExcenta === this.FACTURA_AFECTA,
      fechaFactura: this.formHeader.get('fechaFactura').value,
      incluirEnRecorrido: this.formHeader.get('incluirEnRecorrido').value,
      fechaVencimiento: this.headerData.fechaVencimiento,
      tipo_dte_codigo,
      detailRef: this.documentRef,
      descuentosGlobales: [
        {
          rut_emisor: this.rutEmpresa,
          tipo_dte_codigo,
          es_descuento: true, // descuento: true, recargo: false
          es_porcentaje: descuento_EsPorcentaje,
          monto_descuento: descuento_EsPorcentaje
            ? this.formHeader.get('descuento.porcentaje').value
              ? Number(this.formHeader.get('descuento.porcentaje').value.replace(',', '.'))
              : null
            : Number(this.formHeader.get('descuento.monto').value),
          es_exento: this.formHeader.get('descuento.afectoOrExcento').value !== this.DESCUENTO_ITEM_AFECTO, // Esta condición (!==) aplica solo para DESCUENTOS
        },
        {
          rut_emisor: this.rutEmpresa,
          tipo_dte_codigo,
          es_descuento: false,
          es_porcentaje: recargo_EsPorcentaje,
          monto_descuento: recargo_EsPorcentaje
            ? this.formHeader.get('recargo.porcentaje').value
              ? Number(this.formHeader.get('recargo.porcentaje').value.replace(',', '.'))
              : null
            : Number(this.formHeader.get('recargo.monto').value),

          es_exento: this.formHeader.get('recargo.afectoOrExcento').value !== this.RECARGO_ITEM_AFECTO,
        },
      ],
      sucursal: this.optionsSucursal.filter((p) => p.value === this.formHeader.get('sucursal').value)[0],
      condicionPago: this.formHeader.get('condicionPago').value,
      vendedor: this.optionsVendedor.filter((p) => p.value === this.formHeader.get('vendedor').value)[0],
    };
  }

  disableHeader(tipo: 'LIBRE' | 'NO_FACTURABLE' | 'NORMAL') {
    this.enableClient = false;
    this.formHeader.get('facturaAfectaOrExcenta').disable();
    this.formHeader.get('sucursal').disable();
    this.formHeader.get('condicionPago').disable();
    this.formHeader.get('vendedor').disable();
    // if (tipo === 'NORMAL')
    this.formHeader.get('unidad_negocio_codigo').disable();
  }

  enableContent() {
    this.enableClient = true;
    this.formHeader.get('facturaAfectaOrExcenta').enable();
    this.formHeader.get('sucursal').enable();
    this.formHeader.get('condicionPago').enable();
    this.formHeader.get('vendedor').enable();
    this.formHeader.get('unidad_negocio_codigo').enable();
  }

  //MOSTRAR CONTADOR DE DOCS REFERENCIAS
  getCountListDocRef($event) {
    this.CountListDocRef = $event.length;
    this.documentRef = $event;
  }

  /**
   * Limpia completamente el contenido del formulario y restablece todos los valores a su estado inicial.
   * Este método se ejecuta después de guardar una factura o al presionar el botón limpiar.
   * Asegura que el formulario quede en un estado válido y listo para recibir nueva información.
   *
   * IMPORTANTE: Limpia el estado del modal de selección de cliente para permitir buscar el mismo RUT nuevamente.
   */
  clearContent() {
    this.modalDocsReferencias.clearData();
    this.CountListDocRef = null;
    this.documentRef = [];
    this.isCleaningUp = true;
    this.enableContent();
    this.optionsSucursal = [];
    // this.optionsVendedor = [];
    this.headerData = {};
    this.datePickerFechaDoc.clearDate();

    this.formValidatorsService.reset(this.formHeader);

    // Limpiar el estado del modal de selección de cliente
    // Esto permite buscar el mismo RUT nuevamente después de limpiar el formulario
    this.modalSeleccionarCliente.cleanLast();

    // Resetear el campo RUT explícitamente para permitir pegar contenido nuevamente
    this.formHeader.get('rutCliente').setValue(null, { emitEvent: false });
    const rutControl = this.formHeader.get('rutCliente');
    if (rutControl) {
      rutControl.enable({ emitEvent: false });
      rutControl.markAsUntouched();
      rutControl.markAsPristine();
    }

    this.formHeader.get('sucursal').setValue(null);
    this.formHeader.get('fechaFactura').setValue(null);
    this.formHeader.get('facturaAfectaOrExcenta').setValue(this.FACTURA_AFECTA);
    this.formHeader.get('incluirEnRecorrido').setValue(true);
    this.formHeader.get('vendedor').setValue(null);
    this.formHeader.get('descuento.porcentaje').setValue(null);
    this.formHeader.get('descuento.monto').setValue(null);
    this.formHeader.get('descuento.afectoOrExcento').setValue(this.DESCUENTO_ITEM_AFECTO);
    this.formHeader.get('recargo.porcentaje').setValue(null);
    this.formHeader.get('recargo.monto').setValue(null);
    this.formHeader.get('recargo.afectoOrExcento').setValue(this.RECARGO_ITEM_AFECTO);

    this.formValidatorsService.selectFirstOrDefault(this.formHeader.get('condicionPago'), this.optionsCondicionPago);

    this.formValidatorsService.selectFirstOrDefault(this.formHeader.get('vendedor'), this.optionsVendedor);

    setTimeout(() => {
      this.formHeader.get('fechaFactura').setValue(null);
    }, 500);

    setTimeout(() => {
      this.formHeader.get('fechaFactura').setValue(null);
    }, 1000);

    this.log('clearContent headerData', this.headerData);

    setTimeout(() => {
      this.isCleaningUp = false;
    }, 1000);
  }

  disableUnidadNegocio(unidad_negocio_codigo: number | null) {
    if (unidad_negocio_codigo) this.formHeader.get('unidad_negocio_codigo').setValue(unidad_negocio_codigo);
    this.formHeader.get('unidad_negocio_codigo').disable();
  }
}
